import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import PopupWrapper from 'components/Popups/Wrapper';
import * as S from './styled';

/** Two-letter (or one) initials for avatar tiles — never the full name. */
function getCommunityInitials(raw: string): string {
  const name = raw.trim();
  if (!name) return '?';
  const words = name.split(/\s+/).filter((w) => w.length > 0);
  const pick = (w: string) => {
    const m = w.match(/[a-zA-Z0-9]/);
    return m ? m[0] : w.charAt(0);
  };
  if (words.length >= 2) {
    return (pick(words[0]) + pick(words[1])).toUpperCase();
  }
  const letters = name.replace(/[^a-zA-Z0-9]/g, '');
  if (letters.length >= 2) {
    return (letters[0] + letters[1]).toUpperCase();
  }
  if (letters.length === 1) {
    return letters[0].toUpperCase();
  }
  return name.charAt(0).toUpperCase();
}

export type CommunitySlotRow = {
  id: string;
  name: string;
  logo: string | null;
  subscriptionSlotActive?: boolean;
};

type CommunitySlotsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  maxSlots: number;
  userId: string;
  communities: CommunitySlotRow[];
  /** Pre-select saved ids when reopening; omit to derive from community rows. */
  savedSelection?: string[] | null;
  /** Return `false` to keep the dialog open (e.g. checkout still blocked). */
  onSaved: (selectedIds: string[]) => void | false | Promise<void | false>;
};

function buildInitialSelection(
  rows: CommunitySlotRow[],
  max: number,
): Set<string> {
  const activeFirst = rows.filter((r) => r.subscriptionSlotActive !== false);
  const ids: string[] = [];
  for (const r of activeFirst) {
    if (ids.length >= max) break;
    ids.push(r.id);
  }
  for (const r of rows) {
    if (ids.length >= max) break;
    if (!ids.includes(r.id)) ids.push(r.id);
  }
  return new Set(ids);
}

const CommunitySlotsModal: React.FC<CommunitySlotsModalProps> = ({
  isOpen,
  onClose,
  maxSlots,
  userId,
  communities,
  savedSelection,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || communities.length === 0) return;
    if (savedSelection?.length) {
      const valid = new Set(communities.map((c) => c.id));
      const fromSaved = savedSelection.filter((id) => valid.has(id));
      if (fromSaved.length === maxSlots) {
        setSelected(new Set(fromSaved));
        return;
      }
    }
    setSelected(buildInitialSelection(communities, maxSlots));
  }, [isOpen, communities, maxSlots, savedSelection]);

  const toggle = useCallback(
    (id: string) => {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else if (next.size < maxSlots) {
          next.add(id);
        } else {
          const kept = [...next];
          kept.shift();
          next.clear();
          kept.forEach((x) => next.add(x));
          next.add(id);
        }
        return next;
      });
    },
    [maxSlots],
  );

  const canSave = selected.size === maxSlots && maxSlots > 0;

  const progressPct = useMemo(() => {
    if (!maxSlots || maxSlots <= 0) return 0;
    return Math.min(100, Math.round((selected.size / maxSlots) * 100));
  }, [selected.size, maxSlots]);

  const slotsComplete =
    maxSlots > 0 && selected.size === maxSlots && communities.length > 0;

  const remaining = Math.max(0, maxSlots - selected.size);

  const onSave = async () => {
    if (!canSave || !userId) return;
    setSubmitting(true);
    try {
      const selectedIds = [...selected];
      const closeAfter = await onSaved(selectedIds);
      if (closeAfter !== false) {
        onClose();
      }
    } catch {
      toast.error(t('communitySlots.saveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const empty = useMemo(() => communities.length === 0, [communities.length]);

  const slotCountParams = {
    selected: selected.size,
    max: maxSlots,
  };

  return (
    <PopupWrapper isOpen={isOpen}>
      <S.Panel
        role="dialog"
        aria-modal="true"
        aria-labelledby="community-slots-title"
      >
        <S.PanelHeader>
          <S.HeaderTop>
            <S.Eyebrow>{t('communitySlots.modalEyebrow')}</S.Eyebrow>
            {!empty ? (
              <S.SlotPill $complete={slotsComplete}>
                {t('communitySlots.slotCountShort', slotCountParams)}
              </S.SlotPill>
            ) : null}
          </S.HeaderTop>
          <S.TitleRow>
            <S.TitleIcon aria-hidden>🏠</S.TitleIcon>
            <S.TitleBlock>
              <S.Title id="community-slots-title">
                {t('communitySlots.modalTitle')}
              </S.Title>
              <S.Intro>
                {maxSlots === 1
                  ? t('communitySlots.modalIntroSingle')
                  : t('communitySlots.modalIntro', { max: maxSlots })}
              </S.Intro>
            </S.TitleBlock>
          </S.TitleRow>
          {!empty && maxSlots > 0 ? (
            <S.ProgressBlock>
              <S.BarTrack
                role="progressbar"
                aria-valuenow={selected.size}
                aria-valuemin={0}
                aria-valuemax={maxSlots}
                aria-label={t(
                  'communitySlots.progressAriaLabel',
                  slotCountParams,
                )}
              >
                <S.BarFill $pct={progressPct} />
              </S.BarTrack>
              {slotsComplete ? (
                <S.SelectionHint $ready>
                  {t('communitySlots.selectionReady')}
                </S.SelectionHint>
              ) : (
                <S.SelectionHint>
                  {t('communitySlots.selectionRemaining', {
                    count: remaining,
                  })}
                </S.SelectionHint>
              )}
            </S.ProgressBlock>
          ) : null}
          <S.OnceNote>
            <S.OnceNoteIcon aria-hidden>⏱</S.OnceNoteIcon>
            <S.OnceNoteText>{t('communitySlots.onceOnlyNote')}</S.OnceNoteText>
          </S.OnceNote>
        </S.PanelHeader>
        {empty ? (
          <S.EmptyMessage>
            <S.EmptyIcon aria-hidden>📭</S.EmptyIcon>
            {t('communitySlots.emptyOwned')}
          </S.EmptyMessage>
        ) : (
          <S.ListSection>
            <S.ListHeading>{t('communitySlots.listHeading')}</S.ListHeading>
            <S.List>
              {communities.map((c) => {
                const isOn = selected.has(c.id);
                const isActiveStatus = c.subscriptionSlotActive !== false;
                return (
                  <S.Row
                    key={c.id}
                    $selected={isOn}
                    onClick={() => toggle(c.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggle(c.id);
                      }
                    }}
                    role="checkbox"
                    aria-checked={isOn}
                    tabIndex={0}
                  >
                    <S.AvatarWrap $selected={isOn}>
                      {c.logo ? (
                        <S.Avatar src={c.logo} alt="" />
                      ) : (
                        <S.InitialsAvatar aria-hidden>
                          {getCommunityInitials(c.name)}
                        </S.InitialsAvatar>
                      )}
                    </S.AvatarWrap>
                    <S.RowText>
                      <S.RowName>{c.name}</S.RowName>
                      <S.RowMetaRow>
                        <S.StatusPill $active={isActiveStatus}>
                          {isActiveStatus
                            ? t('communitySlots.statusActive')
                            : t('communitySlots.statusInactive')}
                        </S.StatusPill>
                      </S.RowMetaRow>
                    </S.RowText>
                    <S.SelectionMark $selected={isOn} aria-hidden>
                      {isOn ? '✓' : ''}
                    </S.SelectionMark>
                  </S.Row>
                );
              })}
            </S.List>
          </S.ListSection>
        )}
        <S.Footer>
          <S.BtnGhost type="button" onClick={onClose} disabled={submitting}>
            {t('communitySlots.cancel')}
          </S.BtnGhost>
          <S.BtnPrimary
            type="button"
            disabled={!canSave || submitting || empty}
            onClick={() => void onSave()}
          >
            {submitting ? t('communitySlots.saving') : t('communitySlots.save')}
          </S.BtnPrimary>
        </S.Footer>
      </S.Panel>
    </PopupWrapper>
  );
};

export default CommunitySlotsModal;
